import React, { useMemo, useState } from 'react';
import { Card, DataGrid, Grid, Notification } from 'ui-toolkit';
import { IStandardDocumentData, IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { DocumentCategoriesByName, IDocumentCategory } from '../../types/order/documents/IDocumentCategoryType';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { DocumentBlockFormatters } from './DocumentBlockFormatters';
import { UploadContainer } from './UploadContainer';
import { deleteVendorDocument } from '../../store/vendorStore/vendorDocuments/dispatch';
import { useSelector } from 'react-redux';
import { VendorSelectors } from '../../selectors/vendorPortal/VendorSelectors';
import { Show } from '../shared/Show';
import { isEmpty } from 'lodash';
import { useAppDispatch } from '../../store/store';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

interface IDocumentBlockProps {
  orderId: string;
  documents: IStandardDocumentVersionData[];
  documentCategoriesByName: DocumentCategoriesByName;
  documentCategories: IDocumentCategory[];
  documentCategoryFilter?: (category: IDocumentCategory) => boolean;
}

export const DocumentBlock: React.FC<IDocumentBlockProps> = ({
  documents,
  documentCategoriesByName,
  documentCategories,
  documentCategoryFilter,
  orderId,
}) => {
  const {
    deleteDocumentsFetchStatus: { fetched, fetching, success },
  } = useSelector(VendorSelectors.selectDocuments);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const formatters = useMemo(() => {
    const onDeleteDocumentClick = async (document: IStandardDocumentData) => {
      try {
        setDeleteDocumentId(document.id);
        dispatch(deleteVendorDocument(orderId, document));
        setDeleteDocumentId(null);
      } catch (e) {}
    };
    return DocumentBlockFormatters(documentCategoriesByName, fetching, deleteDocumentId, onDeleteDocumentClick, isZillowClosingEnabled);
  }, [documentCategoriesByName, orderId, fetching, deleteDocumentId, dispatch, isZillowClosingEnabled]);

  const columns: IBootstrapTableColumnProps<IStandardDocumentVersionData>[] = useMemo(() => {
    return [
      { dataField: 'category', text: 'Category', sort: true, formatter: formatters.categoryFormatter },
      {
        dataField: 'uploaderType',
        formatter: formatters.uploaderTypeFormatter,
        text: 'Source',
        headerStyle: { textAlign: 'left', width: '20%' },
        sort: true,
      },
      {
        dataField: 'updatedAt',
        formatter: formatters.updatedAtFormatter,
        text: 'Updated',
        align: 'right',
        headerStyle: { textAlign: 'right', width: '20%' },
        sort: true,
      },
    ];
  }, [formatters]);

  return (
    <Show when={!isEmpty(documentCategoriesByName)}>
      <Card>
        <Grid>
          <Show when={fetched && !success}>
            <Notification type={'error'} message={'File could not removed'} />
          </Show>
          <UploadContainer
            orderId={orderId}
            documentCategoriesByName={documentCategoriesByName}
            documentCategoryFilter={documentCategoryFilter}
          />
          <DataGrid
            defaultSorted={[{ dataField: 'updatedAt', order: 'desc' }]}
            columns={columns}
            data={documents}
            keyField={'id'}
          />
        </Grid>
      </Card>
    </Show>
  );
};
